import React, { useEffect, useState } from 'react';
import axiosInstance from "../../api/axiosInstance";
import TeamMemberCard from './TeamMemberCard';
import { motion, AnimatePresence } from 'framer-motion';

const TeamMembersSection = ({ onClose }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Lock body scroll when component mounts
    document.body.style.overflow = 'hidden';

    const fetchTeamMembers = async () => {
      try {
        const response = await axiosInstance.get('team-members/');
        const selectedMembers = response.data
          .sort((a, b) => a.order - b.order)
          .slice(0, 4);
        setTeamMembers(selectedMembers);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching team members:', err);
        setLoading(false);
      }
    };

    fetchTeamMembers();

    // Cleanup: restore body scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark overlay */}
        <motion.div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Content */}
        <motion.div
          className="relative min-h-screen flex items-center justify-center py-20 px-4"
          initial={{ y: "-100%" }}
          animate={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 100 }}
        >
          <div className="w-full max-w-7xl mx-auto">
            {/* Close button */}
            <motion.button
              className="absolute top-8 right-8 text-white hover:text-gray-300 transition-colors"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>

            {/* Title Section */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Наша команда
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] mx-auto mb-6"></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Профессионалы, которые помогут вам достичь ваших целей
              </p>
            </motion.div>

            {/* Cards Grid */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 overflow-hidden"
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
              initial="hidden"
              animate="show"
            >
              {teamMembers.map((member, index) => (
                <motion.div
                  key={member.id}
                  variants={{
                    hidden: { opacity: 0, y: 50 },
                    show: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        type: "spring",
                        duration: 0.5
                      }
                    }
                  }}
                >
                  <TeamMemberCard member={member} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TeamMembersSection;